import React from 'react';
import { Check, X, RefreshCw, AlertTriangle } from 'lucide-react';
import { CardMatch } from '../../lib/scanner/matcher';

interface ScanResultCardProps {
  match: CardMatch | null;
  alternateMatches?: CardMatch[];
  confidence: number;
  usedAI?: boolean;
  capturedImage?: string;
  onConfirm: (match: CardMatch) => void;
  onSelectAlternate: (match: CardMatch) => void;
  onRetry: () => void;
  onCancel: () => void;
}

export default function ScanResultCard({
  match,
  alternateMatches = [],
  confidence,
  usedAI = false,
  capturedImage,
  onConfirm,
  onSelectAlternate,
  onRetry,
  onCancel
}: ScanResultCardProps) {
  const confidenceColor =
    confidence >= 80 ? 'text-green-400' :
    confidence >= 60 ? 'text-yellow-400' :
    'text-red-400';

  const confidenceLabel =
    confidence >= 80 ? 'High confidence' :
    confidence >= 60 ? 'Medium confidence' :
    'Low confidence';

  if (!match) {
    return (
      <div className="bg-gray-900 rounded-2xl p-6 max-w-md w-full mx-4">
        <div className="text-center mb-6">
          <AlertTriangle className="w-12 h-12 text-yellow-400 mx-auto mb-3" />
          <h3 className="text-xl font-semibold text-white mb-2">
            Card Not Recognized
          </h3>
          <p className="text-gray-400">
            We couldn't identify this card. Try capturing again with better lighting.
          </p>
        </div>

        {capturedImage && (
          <div className="mb-6 rounded-lg overflow-hidden">
            <img
              src={capturedImage}
              alt="Captured"
              className="w-full h-auto max-h-48 object-contain bg-gray-800"
            />
          </div>
        )}

        <div className="flex gap-3">
          <button
            onClick={onCancel}
            className="flex-1 py-3 px-4 bg-gray-800 hover:bg-gray-700 text-white rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onRetry}
            className="flex-1 py-3 px-4 bg-blue-600 hover:bg-blue-500 text-white rounded-lg transition-colors flex items-center justify-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-900 rounded-2xl p-6 max-w-md w-full mx-4">
      {/* Confidence indicator */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div
            className={`w-2 h-2 rounded-full ${
              confidence >= 80 ? 'bg-green-400' :
              confidence >= 60 ? 'bg-yellow-400' :
              'bg-red-400'
            }`}
          />
          <span className={`text-sm ${confidenceColor}`}>
            {confidenceLabel} ({Math.round(confidence)}%)
          </span>
        </div>
        {usedAI && (
          <span className="text-xs text-purple-400 bg-purple-400/10 px-2 py-1 rounded-full">
            AI Identified
          </span>
        )}
      </div>

      {/* Main result */}
      <div className="flex gap-4 mb-6">
        <div className="w-24 h-32 bg-gray-800 rounded-lg overflow-hidden flex-shrink-0">
          {match.imageUrl ? (
            <img
              src={match.imageUrl}
              alt={match.name}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-gray-600">
              No Image
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-xl font-semibold text-white truncate">
            {match.name}
          </h3>
          <p className="text-gray-400 text-sm truncate">
            {match.setName}
          </p>
          <p className="text-gray-500 text-sm mt-1">
            #{match.number}
          </p>
          <p className="text-gray-500 text-xs mt-2">
            ID: {match.cardId}
          </p>
        </div>
      </div>

      {/* Alternate matches */}
      {alternateMatches.length > 0 && confidence < 90 && (
        <div className="mb-6">
          <p className="text-sm text-gray-400 mb-2">Not the right card? Try these:</p>
          <div className="space-y-2">
            {alternateMatches.slice(0, 3).map((alt) => (
              <button
                key={alt.cardId}
                onClick={() => onSelectAlternate(alt)}
                className="w-full flex items-center gap-3 p-2 rounded-lg bg-gray-800 hover:bg-gray-700 transition-colors text-left"
              >
                <div className="w-8 h-10 bg-gray-700 rounded overflow-hidden flex-shrink-0">
                  {alt.imageUrl && (
                    <img
                      src={alt.imageUrl}
                      alt={alt.name}
                      className="w-full h-full object-cover"
                    />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-white truncate">{alt.name}</p>
                  <p className="text-xs text-gray-500 truncate">{alt.setName}</p>
                </div>
                <span className="text-xs text-gray-400">
                  {Math.round(alt.score * 100)}%
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Captured image comparison */}
      {capturedImage && (
        <div className="mb-6">
          <p className="text-xs text-gray-500 mb-2">Your capture:</p>
          <div className="rounded-lg overflow-hidden h-24 bg-gray-800">
            <img
              src={capturedImage}
              alt="Captured"
              className="w-full h-full object-contain"
            />
          </div>
        </div>
      )}

      {/* Action buttons */}
      <div className="flex gap-3">
        <button
          onClick={onCancel}
          className="flex-1 py-3 px-4 bg-gray-800 hover:bg-gray-700 text-white rounded-lg transition-colors flex items-center justify-center gap-2"
        >
          <X className="w-4 h-4" />
          Cancel
        </button>
        <button
          onClick={onRetry}
          className="py-3 px-4 bg-gray-800 hover:bg-gray-700 text-white rounded-lg transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
        <button
          onClick={() => onConfirm(match)}
          className="flex-1 py-3 px-4 bg-green-600 hover:bg-green-500 text-white rounded-lg transition-colors flex items-center justify-center gap-2"
        >
          <Check className="w-4 h-4" />
          Confirm
        </button>
      </div>
    </div>
  );
}
